import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { useModoOscuro } from '@/lib/modoOscuro';
import {
  LayoutDashboard, Building2, Users, Shield, ScrollText,
  Settings, LogOut, ChevronLeft, ChevronRight,
  Globe, Menu, X, Package, Puzzle, BarChart3, Server, LifeBuoy, Layout, ListChecks, BadgeCheck, Moon, Sun, Mail, CreditCard, Calculator, HandCoins, Handshake, MessageCircle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

// EL MENÚ DE SUPER ADMIN, AGRUPADO
//
// Eran 22 entradas planas. Con esa lista uno no elige: recorre. Se agrupan por
// la PREGUNTA que uno trae, no por el módulo técnico que las escribió:
//
//   «¿cuánto entra y cuánto sale?»  → Finanzas
//   «¿qué le vendo y en cuánto?»    → Plan Builder
//   «¿con qué está montado?»        → Infraestructura
//
// Reglas del anidado, y las tres importan:
//
// 1. Un padre con página propia SIGUE siendo un enlace (Plan Builder,
//    Infraestructura). Convertirlo en un simple acordeón le quitaría al dueño
//    una pantalla que ya usaba.
// 2. `Finanzas` NO tiene página: es solo grupo. Por eso va sin `to` — un padre
//    sin página que fuera enlace sería un clic que no lleva a ningún lado.
// 3. Un grupo cuyos hijos estén todos ocultos por permisos se oculta ENTERO.
//    Si no, alguien sin acceso a nada de finanzas vería «Finanzas» vacío y
//    creería que el sistema está roto.
const navItems = [
  // La IA vive dentro del Dashboard: no es una sección que uno visite, es un
  // número que uno mira junto a los demás.
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true, module: 'dashboard',
    paginaPropia: '/admin',
    hijos: [
      { to: '/admin/ai', icon: Shield, label: 'IA', module: 'ai' },
    ] },
  // Los usuarios se administran mirando a qué negocio pertenecen: van juntos.
  { to: '/admin/tenants', icon: Building2, label: 'Tenants', module: 'tenants',
    paginaPropia: '/admin/tenants',
    hijos: [
      { to: '/admin/users', icon: Users, label: 'Usuarios', module: 'users' },
    ] },
  // Un add-on es algo que se le suma a un plan: no se piensa sin el plan.
  { to: '/admin/plans', icon: Package, label: 'Plan Builder', module: 'plans',
    paginaPropia: '/admin/plans',
    hijos: [
      { to: '/admin/addons', icon: Puzzle, label: 'Add-ons', module: 'addons' },
    ] },
  { to: '/admin/usage', icon: BarChart3, label: 'Consumo & Costos', module: 'usage' },
  { to: '/admin/tickets', icon: LifeBuoy, label: 'Soporte', module: 'tickets' },
  { to: '/admin/verticals', icon: Globe, label: 'Verticales', module: 'verticals' },
  { to: '/admin/user-types', icon: BadgeCheck, label: 'Tipo de usuario', module: 'verticals' },
  // Con qué está montada la plataforma: proveedores, números y dominios.
  { to: '/admin/infrastructure', icon: Server, label: 'Infraestructura', module: 'infrastructure',
    paginaPropia: '/admin/infrastructure',
    hijos: [
      { to: '/admin/gupshup-partner', icon: MessageCircle, label: 'Gupshup Partner', ownerRootOnly: true },
      { to: '/admin/landing', icon: Layout, label: 'Landing', ownerRootOnly: true },
      { to: '/admin/web-domains', icon: Globe, label: 'Dominios Web', module: 'infrastructure' },
    ] },
  // Grupo sin página propia: el dinero que entra y el que se debe.
  // Finanzas no tiene página propia: `to` apunta a su primer hijo para que el
  // clic lleve a algún lado, y `esGrupo` hace que desaparezca entero si el
  // usuario no puede ver ninguno de sus hijos.
  { to: '/admin/cobranza', icon: HandCoins, label: 'Finanzas', esGrupo: true,
    hijos: [
      { to: '/admin/cobranza', icon: HandCoins, label: 'Cobranza', module: 'cobranza' },
      { to: '/admin/cobros', icon: CreditCard, label: 'Cobros (tenant)', module: 'settings' },
      // El tarifario es el COSTO de la plataforma: de aquí sale el margen de
      // todo lo que se vende. Va en Finanzas y en un solo sitio — repetirlo
      // bajo Socios sería volver a llenar el menú, que es justo lo que se
      // está quitando.
      { to: '/admin/tarifario', icon: Calculator, label: 'Tarifario', module: 'settings' },
    ] },
  { to: '/admin/socios', icon: Handshake, label: 'Socios', module: 'cobranza' },
  // Configuración deja de ser «(MFA)»: el MFA es UNA de las cosas que se
  // configuran, no el nombre del sitio. Grupo sin página propia: aterriza en
  // su primera pestaña.
  { to: '/admin/settings', icon: Settings, label: 'Configuración', esGrupo: true,
    hijos: [
      { to: '/admin/settings', icon: Settings, label: 'MFA', module: 'settings' },
      { to: '/admin/audit', icon: ScrollText, label: 'Auditoría', module: 'audit' },
      { to: '/admin/mensajes', icon: Mail, label: 'Mensajes del sistema', module: 'settings' },
      { to: '/admin/roadmap', icon: ListChecks, label: 'Tareas sugeridas', module: 'roadmap' },
    ] },
];

/** Ruta → módulo (para bloquear deep-link sin grant). ownerRootOnly se resuelve aparte. */
export const ADMIN_PATH_MODULE = {
  '/admin': 'dashboard',
  '/admin/tenants': 'tenants',
  '/admin/plans': 'plans',
  '/admin/addons': 'addons',
  '/admin/usage': 'usage',
  '/admin/ai': 'ai',
  '/admin/users': 'users',
  '/admin/tickets': 'tickets',
  '/admin/audit': 'audit',
  '/admin/verticals': 'verticals',
  '/admin/user-types': 'verticals',
  '/admin/infrastructure': 'infrastructure',
  '/admin/web-domains': 'infrastructure',
  '/admin/cobros': 'settings',
  '/admin/cobranza': 'cobranza',
  '/admin/socios': 'cobranza',
  '/admin/tarifario': 'settings',
  '/admin/mensajes': 'settings',
  '/admin/roadmap': 'roadmap',
  '/admin/settings': 'settings',
};

export function adminModuleForPath(pathname) {
  if (!pathname) return null;
  if (pathname.startsWith('/admin/tenants/')) return 'tenants';
  if (pathname.startsWith('/admin/gupshup-partner')) return { ownerRootOnly: true };
  if (pathname.startsWith('/admin/landing')) return { ownerRootOnly: true };
  const exact = ADMIN_PATH_MODULE[pathname];
  if (exact) return exact;
  const keys = Object.keys(ADMIN_PATH_MODULE).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (pathname === k || pathname.startsWith(`${k}/`)) return ADMIN_PATH_MODULE[k];
  }
  return null;
}

function AdminModuleGate({ children }) {
  const { canAccessAdminModule, isPlatformOwnerRoot, isPlatformOwner } = useAuth();
  const location = useLocation();
  const need = adminModuleForPath(location.pathname);
  if (need && typeof need === 'object' && need.ownerRootOnly) {
    if (!isPlatformOwnerRoot) {
      return (
        <div className="max-w-lg border rounded-lg p-6 bg-white text-slate-700">
          <h2 className="font-semibold text-lg">Solo el dueño de la plataforma</h2>
          <p className="text-sm mt-2">
            Esta pantalla (Gupshup / Landing) exige el rol <b>platform_owner_root</b>.
            Pídele acceso al dueño o usa otra sección del Super Admin.
          </p>
        </div>
      );
    }
    return children;
  }
  if (need && typeof need === 'string' && !isPlatformOwner
      && canAccessAdminModule?.(need) === false) {
    return (
      <div className="max-w-lg border rounded-lg p-6 bg-white text-slate-700">
        <h2 className="font-semibold text-lg">Sin permiso para este módulo</h2>
        <p className="text-sm mt-2">
          Tu usuario de plataforma no tiene el grant «{need}». Un owner puede
          otorgártelo en Super Admin › Usuarios.
        </p>
        <Button className="mt-4" variant="outline" onClick={() => window.history.back()}>
          Volver
        </Button>
      </div>
    );
  }
  return children;
}


const CLASES_ENLACE = (activo, collapsed, isMobile) =>
  `flex items-center gap-3 mx-2 rounded-lg transition-all text-sm ${
    collapsed && !isMobile ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'
  } ${
    activo
      ? 'bg-white/10 text-white border-l-[3px] border-[#F72585]'
      : 'text-slate-400 hover:text-white hover:bg-white/5 border-l-[3px] border-transparent'
  }`;

/** ¿A qué entrada del menú pertenece esta ruta? */
export function grupoDeLaRuta(ruta, items = navItems) {
  for (const item of items) {
    const rutas = [item.to, ...(item.hijos || []).map((h) => h.to)].filter(Boolean);
    // El Dashboard es '/admin' y casaría con todo: solo exacto.
    const casa = rutas.some((r) => (r === '/admin' ? ruta === '/admin' : ruta.startsWith(r)));
    if (casa) return item;
  }
  return null;
}

/**
 * Las pestañas del grupo, DENTRO de la página.
 *
 * Antes esto eran submenús que se desplegaban en la barra. Funcionaba, pero la
 * barra crecía al abrirlos y el problema que se quería resolver era justo ese:
 * un menú gigante. Aquí la barra nunca cambia de alto —solo padres— y el
 * detalle del grupo vive donde ya estás mirando.
 *
 * Se pinta SOLO si el grupo tiene hijos: una pestaña única no es una elección,
 * es ruido.
 */
export function PestanasDelGrupo({ ruta, items = navItems }) {
  const grupo = grupoDeLaRuta(ruta, items);
  if (!grupo || !(grupo.hijos || []).length) return null;

  // El padre entra como una pestaña más cuando tiene página propia. Si no la
  // tiene (Finanzas), no se inventa: sería una pestaña que no lleva a nada.
  const pestanas = [
    ...(grupo.paginaPropia ? [{ to: grupo.paginaPropia, label: grupo.label, icon: grupo.icon }] : []),
    ...grupo.hijos,
  ];

  return (
    <div className="flex items-center gap-1 border-b border-[#E2E8F0] mb-5 overflow-x-auto"
         data-testid={`pestanas-${grupo.label.toLowerCase().replace(/\s/g, '-')}`}>
      {pestanas.map((t) => {
        const activa = t.to === '/admin' ? ruta === '/admin' : ruta.startsWith(t.to);
        return (
          <NavLink key={t.to} to={t.to} end={t.to === '/admin'}
            data-testid={`pestana-${t.label.toLowerCase().replace(/\s/g, '-')}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
              activa
                ? 'border-[#F72585] text-[#0F172A] font-medium'
                : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
            }`}>
            <t.icon className="w-4 h-4" strokeWidth={1.5} />
            {t.label}
          </NavLink>
        );
      })}
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout, isImpersonating, canAccessAdminModule, isPlatformOwnerRoot } = useAuth();
  // Super Admin no tenía modo oscuro. No estaba roto —al salir del panel del
  // negocio la clase se quita sola— simplemente no existía, y quien administra
  // la plataforma pasa aquí tantas horas como un dueño en el suyo.
  const [oscuro, alternarOscuro] = useModoOscuro();
  const puedeVer = (item) => {
    if (item.ownerRootOnly) return !!isPlatformOwnerRoot;
    if (!item.module) return true;
    return canAccessAdminModule?.(item.module) !== false;
  };
  const visibleNav = navItems
    .map((item) => ({ ...item, hijos: (item.hijos || []).filter(puedeVer) }))
    .filter((item) => {
      // Un grupo sin página propia se oculta ENTERO si no le queda ningún hijo
      // visible. Si no, alguien sin acceso a nada de finanzas vería «Finanzas»
      // y al entrar chocaría con una pantalla que no puede ver.
      if (item.esGrupo) return item.hijos.length > 0;
      return puedeVer(item);
    });
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = ({ isMobile }) => (
    <>
      {/* Logo */}
      <div className={`h-14 md:h-16 flex items-center border-b border-white/10 ${collapsed && !isMobile ? 'justify-center px-2' : 'px-5'}`}>
        {(!collapsed || isMobile) ? (
          <h1 className="text-lg font-bold text-white tracking-tight">
            Cavernicol<span className="text-[#F72585]">App</span>
          </h1>
        ) : (
          <span className="text-lg font-bold text-[#F72585]">C</span>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="ml-auto text-slate-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Platform badge */}
      {(!collapsed || isMobile) && (
        <div className="px-5 py-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#F72585] font-semibold">
            Super Admin
          </span>
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 py-2 space-y-0.5 overflow-y-auto">
        {/* Solo los padres. La barra no crece nunca: el detalle del grupo se
            ve en pestañas dentro de la página. */}
        {visibleNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={() => CLASES_ENLACE(
              grupoDeLaRuta(location.pathname, visibleNav)?.label === item.label,
              collapsed, isMobile)}
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
            {(!collapsed || isMobile) && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User / Collapse */}
      <div className="border-t border-white/10 p-3">
        {(!collapsed || isMobile) && (
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#F72585] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-400 truncate">{user?.platform_role?.replace(/_/g, ' ')}</p>
            </div>
          </div>
        )}
        <div className={`flex ${collapsed && !isMobile ? 'flex-col items-center gap-2' : 'gap-2'}`}>
          {!isMobile && (
            <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)}
              className="text-slate-400 hover:text-white hover:bg-white/10 hidden md:flex" data-testid="toggle-sidebar">
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout}
            className="text-slate-400 hover:text-white hover:bg-white/10" data-testid="logout-button">
            <LogOut className="w-4 h-4" />
            {(!collapsed || isMobile) && <span className="ml-2 text-sm">Salir</span>}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F9FA]" data-testid="admin-layout">
      {isImpersonating && <ImpersonationBanner />}

      {/* Desktop Sidebar */}
      <aside
        className={`${collapsed ? 'w-16' : 'w-64'} bg-[#0F172A] hidden md:flex flex-col transition-all duration-200 flex-shrink-0`}
        data-testid="admin-sidebar"
      >
        <SidebarContent isMobile={false} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#0F172A] flex flex-col z-10 animate-fade-in-up">
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        <header className="h-14 md:h-16 flex items-center justify-between px-4 md:px-6 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0] sticky top-0 z-40" data-testid="admin-topbar">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Mobile hamburger */}
            <button onClick={() => setMobileOpen(true)} className="md:hidden p-1.5 text-[#64748B] hover:text-[#0F172A]" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
            {/* Mobile logo */}
            <span className="md:hidden text-base font-bold text-[#0F172A] tracking-tight">
              C<span className="text-[#F72585]">A</span>
            </span>
            <span className="hidden sm:inline text-sm text-[#64748B] truncate">
              Super Admin
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={alternarOscuro}
              title={oscuro ? 'Volver al modo claro' : 'Cambiar a modo oscuro'}
              aria-label={oscuro ? 'Volver al modo claro' : 'Cambiar a modo oscuro'}
              className="p-2 text-[#64748B] hover:text-[#0F172A] transition-colors"
              data-testid="admin-modo-oscuro"
            >
              {oscuro ? <Sun className="w-5 h-5" strokeWidth={1.5} /> : <Moon className="w-5 h-5" strokeWidth={1.5} />}
            </button>
            <div className="h-5 w-px bg-[#E2E8F0] hidden sm:block" />
            <div className="hidden sm:flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#F72585]" strokeWidth={1.5} />
              <span className="text-xs text-[#64748B] font-medium">
                {user?.platform_role?.replace(/_/g, ' ')?.toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        {/* Page content — gate por módulo (owners siempre; platform_admin por grant) */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="animate-fade-in-up">
            <PestanasDelGrupo ruta={location.pathname} items={visibleNav} />
            <AdminModuleGate>
              <Outlet />
            </AdminModuleGate>
          </div>
        </div>
      </main>
    </div>
  );
}
